import {closeDialog, openDialog} from 'platform/components';
import {Text} from 'platform/foundation';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

export type VehicleChangePriceStrategy = 'update' | 'keep';

const DIALOG_ID = 'directSaleBulkRecalculation';

/**
 * Opens the bulk recalculation prompt shown when the vehicle on a Direct Sale with existing
 * items is changed. Resolves with the chosen price strategy, or `null` when the dialog is
 * cancelled/closed (in which case the vehicle change should be reverted by not applying it).
 */
export const openBulkRecalculationDialog = (props: TestIdProps) =>
  new Promise<VehicleChangePriceStrategy | null>((resolve) => {
    // Resolve on click rather than in onCloseComplete: the dialog store can drop a dialog
    // without ever firing onCloseComplete (route change, replacement by a same-id dialog),
    // which would leave the promise pending and silently swallow the chosen strategy.
    //
    // The later resolve(null) from onCloseComplete is a no-op for an already-settled promise.
    const select = (strategy: VehicleChangePriceStrategy) => {
      resolve(strategy);
      closeDialog(DIALOG_ID);
    };

    openDialog(
      <Text data-testid={suffixTestId('bulkRecalculationDialog.message', props)}>
        {i18n.t('entity.warehouse.labels.vehicleChangeRecalculationMessage')}
      </Text>,
      {
        id: DIALOG_ID,
        title: i18n.t('entity.warehouse.labels.vehicleChangeRecalculationTitle'),
        size: 'small',
        onCloseComplete: () => resolve(null),
        buttons: [
          {
            variant: 'secondary',
            title: i18n.t('entity.warehouse.actions.keepCurrentPrices'),
            onClick: () => select('keep'),
            'data-testid': suffixTestId('bulkRecalculationDialog.keepCurrentPrices', props),
          },
          {
            variant: 'primary',
            title: i18n.t('entity.warehouse.actions.updateAllPrices'),
            onClick: () => select('update'),
            'data-testid': suffixTestId('bulkRecalculationDialog.updateAllPrices', props),
          },
        ],
        'data-testid': suffixTestId('bulkRecalculationDialog', props),
      }
    );
  });
